import { Api, ArrayUtil, Mutex, ObjectUtil, StringUtil } from "@mcleod/core";
import { DatabaseMenuItem } from "./DatabaseMenuItem";
import { RowWebMenuGroup } from "../models/ModelWebMenuGroup";
import { RowWebMenuItem } from "../models/ModelWebMenuItem";

const ENDPOINT = "lib/menu-editor";

export type DataChangeEvent = {
    hasChanged: boolean;
    loadingData: boolean;
}

export class MenuDataSource {
    private _postMutex = new Mutex();
    private _menuData: DatabaseMenuItem;
    private _executedChanges: string[] = [];
    private _changedDataCallbacks: (() => void)[] = [];
    private _loadingData: boolean = false;
    private _deletedGroups: Map<string, RowWebMenuGroup> = new Map();
    private _deletedItems: Map<string, RowWebMenuItem> = new Map();

    hasChanged(): boolean {
       return this.menuData?.hasChanged() ||
       this._deletedGroups.size > 0 ||
       this._deletedItems.size > 0;
    }

    isLoadingData(): boolean {
        return this._loadingData;
    }

    public get menuData(): DatabaseMenuItem {
        return this._menuData;
    }

    public get executedChanges(): string[] {
        return [...this._executedChanges];
    }

    public get deletedGroups(): Map<string, RowWebMenuGroup> {
        return new Map(this._deletedGroups);
    }
    public get deletedItems(): Map<string, RowWebMenuItem> {
        return new Map(this._deletedItems);
    }

    deleteMenuItem(menuItem: DatabaseMenuItem) {
        if (menuItem.isMainMenu) {
            return;
        }

        this.deleteItemIfNotReferenced(menuItem);
        if (menuItem.groupReferenceData) {
            this.deleteGroupIfNotReferenced(menuItem);
            this.deleteGroup(menuItem.groupReferenceData);
        } else {
            this.deleteGroup(menuItem.groupData);
        }

        for (const child of menuItem.items ?? []) {
            this.deleteMenuItem(child);
        }
    }

    private deleteItemIfNotReferenced(menuItem: DatabaseMenuItem) {
        if (menuItem.itemData._appending === true) {
            return;
        }
        const itemId = menuItem.itemData.get("id");
        const reference = this.menuData.findFirstItem((item) => item != menuItem && item.itemData.get("id"));
        if (reference == null) {
            this._deletedItems.set(itemId, menuItem.itemData);
        }
    }

    private deleteGroupIfNotReferenced(menuItem: DatabaseMenuItem) {
        const groupId = menuItem.groupData.get("id");
        const references = this.menuData.getRecursiveItems((item) => item != menuItem && item.groupData.get("id") == groupId);

        if (ArrayUtil.isEmptyArray(references)) {
            // no references to the shared menu, delete it
            this.deleteGroup(menuItem.groupData);
        } else if (references.length == 1) {
            // Only one reference remains - convert shared menu to standard branch
            const groupToDelete = menuItem.convertFromShared();
            this.deleteGroup(groupToDelete);
        }
    }

    deleteGroup(group: RowWebMenuGroup) {
        if (!StringUtil.isEmptyString(group?.get("id")) && group?._appending === false) {
            this._deletedGroups.set(group.get("id"), group);
        }
    }

    // Finds existing DatabaseMenuItems with matching web_menu_item data.
    // If found, reuses the web_menu_item instead of creating duplicates.
    findAndReuseExistingMenuItemData(menuItem: DatabaseMenuItem): DatabaseMenuItem {
        if (menuItem == null)
            return null;
        const menuItemRowData = menuItem.itemData.data;
        const excludeFields = ["id", "company_id"]
        const matchFunc = (item: DatabaseMenuItem) => item.itemData.isDataEqual(menuItemRowData, excludeFields);
        const existingItemRow = this.menuData.findFirstItem(matchFunc)?.itemData;
        if (existingItemRow != null) {
            menuItem.itemData = { ...existingItemRow.data };
            menuItem.itemData._appending = existingItemRow._appending;
            menuItem.groupData.set("item_id", menuItem.itemData.get("id"));
        }
        return menuItem;
    }

    private clearDeletedGroups(): void {
        this._deletedGroups.clear();
        this._deletedItems.clear();
    }

    async loadData() {
        return this.setDataFromResponse(await Api.search(ENDPOINT));
    }

    async post(): Promise<void> {
        const release = await this._postMutex.acquire();
        try {
            const body = this.serializeData();

            if (ObjectUtil.isEmptyObject(body, true)) {
                return;
            }

            const response = await Api.post(ENDPOINT, JSON.stringify(body));
            this.setDataFromResponse(response);

        } finally {
            release();
        }
    }

    private syncItemPositions() {
        this.menuData?.items?.forEach((child, index) => {
            const group = child.groupReferenceData ?? child.groupData;
            group.set("item_position", index);
            // only top-level items shold have item_position set
            child.recurseItems((item) => {
                item.groupData.set("item_position", undefined);
                if (item.groupReferenceData != null) {
                    item.groupReferenceData.set("item_position", undefined);
                }
            });
        });
    }

    private serializeData(): any {
        if (this.hasChanged() === false) {
            return {};
        }

        this.syncItemPositions();

        const uniqueGroups = new Map<string, any>();
        const uniqueItems = new Map<string, any>();

        this.menuData.recurseItems(child => {
            const groupData = child.groupData;
            const groupReferenceData = child.groupReferenceData;
            const itemData = child.itemData;

            if (groupData.hasChanged()) {
                uniqueGroups.set(groupData.get("id"), groupData);
            }

            if (groupReferenceData?.hasChanged()) {
                uniqueGroups.set(groupReferenceData.get("id"), groupReferenceData);
            }

            if (itemData.hasChanged()) {
                uniqueItems.set(itemData.get("id"), itemData);
            }
        });

        const result = {
            deleted_menu_groups: Array.from(this._deletedGroups.keys()).map(id => ({id})),
            deleted_menu_items: Array.from(this._deletedItems.keys()).map(id => ({id})),
            menu_group_rows: Array.from(uniqueGroups.values()),
            menu_item_rows: Array.from(uniqueItems.values())
        };

        return ObjectUtil.deleteNullOrEmptyProps(result);
    }

    private setDataFromResponse(response: any): void {
        try {
            this._loadingData = true;
            this._menuData = new DatabaseMenuItem({
                ...response.menu,
                isMainMenu: true
            }, false);
            this.addExecutedChanges(response.executed_changes);
            this.clearDeletedGroups();
            this.fireChangedDataCallbacks();
        } finally {
            this._loadingData = false;
        }
    }

    private addExecutedChanges(executedChanges: any) {
        const executedChangesArray = ArrayUtil.getAsArray(executedChanges, true);
        if (!ArrayUtil.isEmptyArray(executedChangesArray)) {
            this._executedChanges.push(...executedChangesArray, "\n");
        }
    }

    public addChangedDataCallback(callback: () => void) {
        this._changedDataCallbacks.push(callback);
    }

    public notifyDataChanged() {
        this.fireChangedDataCallbacks();
    }

    private fireChangedDataCallbacks() {
        this._changedDataCallbacks.forEach(cb => cb());
    }
}
